import React, { useState } from "react";
import { GridApiPro } from "@mui/x-data-grid-pro";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { InstalledBaseEventList } from "../../graphql/queries/installedBaseEventList";
import ExportMenuButton from "../ExportMenuButton";
import { readGridColumnLayout } from "../Table/gridColumnLayout";
import { EXCEL_DATE_TIME_FORMAT, ExportColumnDef, ExportFormat, exportRows, toExcelDate } from "../../utils/listExport";
import { ExportMode, orderExportColumns } from "../../utils/tableColumnLayout";
import { useSnackbar } from "../../provider/snackbar";
import { TypeDefinitionAttribute } from "../../types";
import moment from "moment/moment";
import formatEventEnum from "../../utils/formatEventEnum";
import { getExtraEventColumns } from "../AssetEventList/eventExtraColumns";

const EXPORT_PAGE_SIZE = 100;

// Default order = the global event list's default column order (Event ID leads)
const getExportColumns = (t: any): ExportColumnDef[] => {
    const extraColumns = getExtraEventColumns(t);
    return [
        ...extraColumns.filter((c) => c.field === "eventId"),
        {
            field: "eventType",
            headerName: t("Event"),
            getValue: (row) => row.eventType ? formatEventEnum(row.eventType, t) : ""
        },
        {
            field: "insert",
            headerName: t("Date"),
            getValue: (row) => row.insert ? moment(row.insert).format('L LT') : "",
            getTypedValue: (row) => row.insert ? toExcelDate(moment(row.insert)) : null,
            excelFormat: EXCEL_DATE_TIME_FORMAT,
        },
        {
            field: "user",
            headerName: t("Author"),
            getValue: (row) => row?.user?.name || ""
        },
        {
            field: "eventCategory",
            headerName: t("Category"),
            getValue: (row) => row.eventCategory ? formatEventEnum(row.eventCategory, t) : "",
        },
        {
            field: "asset",
            headerName: t("Asset"),
            getValue: (row) => {
                const asset = row.asset;
                if (!asset) return "";
                const name = asset.product?.name;
                const serial = asset.serialNumber;
                const parts = [];
                if (name) parts.push(name);
                if (serial) parts.push(`(S/N: ${serial})`);
                return parts.join(" ");
            },
        },
        {
            field: "message",
            headerName: t("Message"),
            getValue: (row) => row.message || ""
        },
        // Additional Semicon export columns (hidden in the table by default).
        ...extraColumns.filter((c) => c.field !== "eventId"),
    ];
}


interface Props {
    filter?: Record<string, any>;
    searchParams?: { query: string; fields: string[] };
    attributes?: TypeDefinitionAttribute[];
    // The table's grid, where it offers column management (global event list): the
    // export columns then follow its order and visibility. Without it everything is
    // exported in the default order.
    apiRef?: React.MutableRefObject<GridApiPro>;
}

const EventListExportButton = ({ filter, searchParams, attributes, apiRef }: Props) => {
    const { t } = useTranslation();
    const client = useApolloClient();
    const snackbar = useSnackbar();
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = (mode: ExportMode, format: ExportFormat) => {
        // Read before the async fetch: the columns the user sees when clicking
        const exportColumns = orderExportColumns(getExportColumns(t), readGridColumnLayout(apiRef), mode);

        setIsExporting(true);
        snackbar.startLocalProgress(t("Preparing export..."));

        // Capture snackbar methods in closure so they survive component unmount
        const { updateLocalProgress, finishLocalProgress, errorLocalProgress } = snackbar;

        (async () => {
            try {

                // Fetch first page to get total
                const firstResult = await client.query({
                    query: InstalledBaseEventList,
                    variables: {
                        filter: filter,
                        search: searchParams,
                        pagination: { start: 0, limit: EXPORT_PAGE_SIZE },
                    },
                    context: { clientName: "federatedGraphEndpoint" },
                    fetchPolicy: "network-only",
                });

                const total: number = firstResult.data.installedBaseEventList.total;
                let allRows: any[] = [
                    ...firstResult.data.installedBaseEventList.installedBaseEvents,
                ];
                updateLocalProgress(allRows.length, total);

                // Fetch remaining pages
                for (
                    let start = EXPORT_PAGE_SIZE;
                    start < total;
                    start += EXPORT_PAGE_SIZE
                ) {
                    const result = await client.query({
                        query: InstalledBaseEventList,
                        variables: {
                            filter: filter,
                            search: searchParams,
                            pagination: { start, limit: EXPORT_PAGE_SIZE },
                        },
                        context: { clientName: "federatedGraphEndpoint" },
                        fetchPolicy: "network-only",
                    });
                    allRows = [
                        ...allRows,
                        ...result.data.installedBaseEventList.installedBaseEvents,
                    ];
                    updateLocalProgress(allRows.length, total);
                }

                await exportRows({
                    format,
                    columns: exportColumns,
                    rows: allRows,
                    fileBaseName: "events-export",
                    sheetName: t("Events"),
                });

                finishLocalProgress(t("Export complete"));
            } catch {
                errorLocalProgress(t("Export failed"));
            } finally {
                setIsExporting(false);
            }
        })();
    };

    return (
        <ExportMenuButton
            onExport={handleExport}
            disabled={isExporting}
            testId="event-export-button"
            withModeMenu={!!apiRef}
        />
    );
};

export default EventListExportButton;
